import { Fragment } from 'react';
import {
	PagedDialog,
	H3,
	H4,
	Spacer,
	Text,
	Label,
	TempChip,
	InfoChip,
	Loader,
	H2,
	Tooltip
} from '@components';
import {
	TShipmentOrder,
	CHIP_SIZE,
	TRateCard,
	ACCESS_LEVELS,
	TOOLTIP_POSITION,
	TReorderForm,
} from '@types';
import { formatPriceToFinancial, groupCargosByStop } from '@utils';
import styles from './ReorderShipment.module.scss';
import { authenticatedUser } from '@state';
import { useRecoilValue } from 'recoil';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';

type TProps = {
	shipment: TShipmentOrder | null;
	reorderForm: TReorderForm;
	priceData: TRateCard | null;
	loadingBookingPrices: boolean;
	isReturn: boolean;
}

export const Preview = (props: TProps) => {
	const {
		shipment,
		priceData,
		reorderForm,
		loadingBookingPrices,
		isReturn,
	} = props;
	const authUser = useRecoilValue(authenticatedUser);
	const roleHasPrice = (): boolean => {
		const accessLevel = authUser?.team?.access_level ?? ACCESS_LEVELS.GUEST;
		return (accessLevel <= ACCESS_LEVELS.TEAM);
	}
	const getLoadTruckDetails = () => {
		const members: string[] = [];
		if (shipment) {
			if (shipment.cargo_type && shipment.cargo_type !== '') members.push(shipment.cargo_type);
			if (shipment.req_truck_size && shipment.req_truck_size.name) members.push(shipment.req_truck_size.name);
			if (shipment.req_truck_type && shipment.req_truck_type.name) members.push(shipment.req_truck_type.name);
		}
		return members.join(' | ');
	}
	const getDateTimes = (dest: 'pickup' | 'dropoff') => {
		const timeFrom = dest === 'pickup' ? reorderForm?.stops[0].startTime : reorderForm?.stops[1].startTime;
		const timeTo = dest === 'pickup' ? reorderForm?.stops[0].endTime : reorderForm?.stops[1].endTime;
		return `${timeFrom} - ${timeTo}`;
	}

	const getDestDetails = (dest: 'pickup' | 'dropoff', index: number) => {
		const getProperDest = () => {
			if (!isReturn) return dest;
			if (dest === 'pickup') {
				return 'dropoff';
			}
			return 'pickup';
		}
		return (
			<>
				<div className={`${styles.row} ${styles.spaceBetween}`}>
					<Label>{shipment?.cargos[index][getProperDest()].business_name}</Label>
					<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} size={CHIP_SIZE.SMALL} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} />
				</div>
				<Text>{shipment?.cargos[index][getProperDest()].address}</Text>
				<Text>{getDateTimes(dest)}</Text>
			</>
		)
	}

	const buildPriceGrid = () => {
		if (!priceData) return;
		const priceItems: { label: string; value: string; attractsFuelLevy?: boolean }[] = [];
		const { price } = priceData;
		const modifiers = priceData.appliedModifiers

		if (price.chargeableUnits !== null) priceItems.push({ label: 'Chargeable Weight', value: price.chargeableUnits });
		if (price.calculatedPrice !== null) priceItems.push({ label: 'Calculated Price', value: `$${formatPriceToFinancial(price.calculatedPrice)}` });
		if (price.basePrice !== null) priceItems.push({ label: 'Base Price', value: `$${formatPriceToFinancial(price.basePrice)}` });
		if (price.minCharge !== null) priceItems.push({ label: 'Minimum Price', value: `$${formatPriceToFinancial(price.minCharge ?? '0')}` });
		if (price) priceItems.push({ label: 'Price', value: `$${formatPriceToFinancial(`${price.subTotalPrice}`)}` });

		if (modifiers?.length > 0) {
			modifiers.forEach((modifier) => {
				priceItems.push({
					label: modifier.description, value: `$${formatPriceToFinancial(modifier.value)}`, attractsFuelLevy: !!modifier.attractsFuelLevy
				});
			});
		}

		const fuelLevyApplicableModifierIndex = priceItems.findIndex((item) => {
			return item.attractsFuelLevy === false;
		});

		if (priceData && priceData.fuelLevy.amount) {
			const fuelLevyItem = { label: 'Fuel Levy', value: `$${formatPriceToFinancial(`${priceData?.fuelLevy.amount}`)}` };
			// Show Fuel Levy before the first modifier that doesn't attract fuel levy
			if (fuelLevyApplicableModifierIndex !== -1) {
				priceItems.splice(fuelLevyApplicableModifierIndex, 0, fuelLevyItem);
			} else {
				priceItems.push(fuelLevyItem);
			}
		}

		return <>
			{priceItems.map((item, i) => {
				return <div className={styles.priceRow} key={i}>
					<Label>
						{item.label}{' '}
						{item.attractsFuelLevy ? (
							<Tooltip
								position={TOOLTIP_POSITION.RIGHT}
								text='Fuel Levy applies on this modifier'
							>
								<LocalGasStationOutlinedIcon className={styles.fuelLevyIcon} />
							</Tooltip>
						) : null}{' '}
					</Label>
					<Text>
						{item.value}
					</Text>
				</div>
			})}
		</>
	}

	return (
		<PagedDialog.Preview>
			<H3>Booking Details</H3>
			<Spacer size={20} divider />
			<H4>{reorderForm.orderNumber}</H4>
			<Spacer size={8} />
			<div className={styles.row}>
				<TempChip name={shipment?.temperature_id ?? 1} size={CHIP_SIZE.SMALL} />
				<Text>{' | '}{getLoadTruckDetails()}</Text>
			</div>
			<Spacer size={20} divider />
			{/* Cargos collected from the same place are one stop, and there is a single drop-off
				however many freight lines the shipment carries. */}
			{groupCargosByStop(shipment?.cargos ?? []).pickups.map((group, i) => {
				return (
					<Fragment key={i}>
						{getDestDetails('pickup', group[0])}
						<Spacer size={20} />
					</Fragment>
				)
			})}
			{!!shipment?.cargos.length && getDestDetails('dropoff', shipment.cargos.length - 1)}
			<Spacer size={32} divider />
			{
				loadingBookingPrices && <Loader />
			}
			{
				roleHasPrice() && <>
					<H4>Price</H4>
					<Spacer size={8} />
					{
						!loadingBookingPrices && (
							priceData ?
								<>
									{buildPriceGrid()}
									<Spacer size={8} />
									<div className={styles.priceRow}>
										<Label>Total</Label>
										<H2>${formatPriceToFinancial(priceData.totalPrice)} + GST</H2>
									</div>
								</> :
								<Text>Price will show here once all fields are entered</Text>
						)
					}
				</>
			}
		</PagedDialog.Preview>
	);
}
Preview.displayName = 'Preview';
